import {useContext, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Grid, Typography} from '@mui/material';

import {ReactComponent as ArrowIcon} from '../../../../../assets/arrowIcon.svg';
import {BoxList} from '../../../../../components/BoxList/BoxList';
import {CultureTypeImage} from '../../../../../components/CultureTypeImage/CultureTypeImage';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {CultureInformation} from '../../../../company/culture/dashboard/types/CultureInformationTypes';

interface Props {
  cultureInformation: CultureInformation;
  onClick?: () => void;
  shouldHideGradientColor?: boolean;
  backgroundColor?: string;
  detailedCardView?: boolean;
  open?: boolean;
}

export const CultureOverview = ({
  cultureInformation,
  onClick,
  shouldHideGradientColor,
  backgroundColor,
  detailedCardView,
  open,
}: Props) => {
  const {t} = useTranslation();

  const currentLanguage = useContext(LanguageContext);

  const [isHover, setIsHover] = useState<boolean>(false);

  const words = cultureInformation.cultureType.keywords[currentLanguage].split(',').map((str) => str.trim());
  const cultureType = cultureInformation.cultureType.name[currentLanguage];

  return (
    <Grid
      container
      sx={{
        minHeight: '121px',
        borderRadius: '10px',
        backgroundColor: backgroundColor || 'background.paper',
        ...(!shouldHideGradientColor && {
          backgroundImage: 'linear-gradient(to left, #FFFFFF 50%, #E7E2DE 100%)',
        }),
        padding: {xs: '12px', md: '20px'},
        cursor: onClick ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      {detailedCardView && (
        <Box width={'100%'} display={'flex'} justifyContent={'flex-end'}>
          <ArrowIcon
            fill={orangeColors[100]}
            style={{
              transform: open ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease-in-out',
              height: '30px',
              width: '30px',
            }}
          />
        </Box>
      )}
      <Grid
        container
        item
        xs={12}
        sx={{
          alignItems: 'center',
          justifyContent: {xs: 'center', sm: 'space-between'},
          flexDirection: {xs: 'column', sm: 'row'},
          gap: {xs: 2, sm: 0},
        }}
      >
        <Box
          display={'flex'}
          gap={1}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          sx={{
            flexDirection: {xs: 'column', sm: 'row'},
            alignItems: 'center',
            textAlign: {xs: 'center', sm: 'left'},
            width: {xs: '100%', sm: 'auto'},
            ...(onClick &&
              !detailedCardView && {
              '&:hover': {
                transform: 'scale(1.03)',
                transition: 'transform 0.2s ease-in-out',
                transformOrigin: '0 0',
                cursor: 'pointer',
              },
            }),
          }}
          onClick={detailedCardView ? onClick : undefined}
        >
          <CultureTypeImage
            src={cultureInformation.cultureType.imageUrl}
            sx={{
              marginTop: 0,
              marginBottom: {xs: 0, sm: 3, lg: 0},
              height: {xs: '137px', lg: '147px'},
            }}
          />
          <Box>
            <Typography variant='body1'>{t('shared.cultureType')}</Typography>
            <Typography
              variant='h1'
              sx={{
                color: isHover && onClick && !detailedCardView ? orangeColors[40] : '',
                fontSize: {xs: '24px', sm: '32px', lg: '40px'},
              }}
            >
              {cultureType}
            </Typography>
          </Box>
        </Box>

        <Box sx={{width: {xs: '100%', sm: 'auto'}}}>
          <BoxList
            data={words}
            stackOnMobile
            onClick={detailedCardView ? onClick : undefined}
            sx={{
              padding: '16px 18px',
              marginTop: 2,
              ...(onClick &&
                !detailedCardView && {
                '&:hover': {
                  transform: 'scale(1.03)',
                  transition: 'transform 0.2s ease-in-out',
                  transformOrigin: '0 0',
                  cursor: 'pointer',
                  backgroundColor: orangeColors[40],
                  color: 'white',
                },
              }),
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
};
